import React from 'react';
import { TOOL_BRAND_ASSETS } from '@tool-brand';
import apiService from '../../services/ApiService';
import { ToolContentSlot } from '../tool-content/contentModel';
import { MenuOption } from './OptionMenu';

/** Tool come lo restituisce GET api/admin/tools. */
export interface AdminTool {
    id: number;
    name: string;
    code: string;
    description?: string;
    iconName?: string;
    category?: string;
    toolType: string;
    catalogScope: string;
    status: string;
    visibility: string;
    audience: string;
    displayOrder: number;
    assignedUserCount: number;
    guideLanguages: string[];
    imageRoles: string[];
    openReportCount: number;
    contentSlots: ToolContentSlot[];
}

export type FieldKey = 'status' | 'visibility' | 'catalogScope' | 'toolType';

export interface FieldDef {
    key: FieldKey;
    label: string;
    /** Pillola colorata (stato, catalogo) o testo semplice. */
    pill: boolean;
    options: (MenuOption & { tone?: string })[];
}

// Colori dei pallini: gli stessi valori dei token --et-* (success, warning, petrolio).
export const STATUS_FIELD: FieldDef = {
    key: 'status', label: 'Stato', pill: true, options: [
        { value: 'Active', label: 'Attivo', dot: '#1f8a4c', tone: 'success' },
        { value: 'Debug', label: 'Debug', dot: '#d99a1e', tone: 'warning' },
        { value: 'Inactive', label: 'Inattivo', dot: '#9aa5b6', tone: 'muted' },
    ],
};
export const VISIBILITY_FIELD: FieldDef = {
    key: 'visibility', label: 'Visibilità', pill: false, options: [
        { value: 'Public', label: 'Tutti gli utenti', dot: '#12616a' },
        { value: 'Restricted', label: 'Su invito', dot: '#d99a1e' },
    ],
};
export const CATALOG_FIELD: FieldDef = {
    key: 'catalogScope', label: 'Catalogo', pill: true, options: [
        { value: 'EasyTools', label: 'Semplora', dot: '#12303f', tone: 'brand' },
        { value: 'Common', label: 'Comune', dot: '#9aa5b6', tone: 'muted' },
    ],
};
export const TYPE_FIELD: FieldDef = {
    key: 'toolType', label: 'Tipo', pill: false, options: [
        { value: 'Development', label: 'Sviluppo' },
        { value: 'Administration', label: 'Amministrazione' },
        { value: 'Customization', label: 'Personalizzazione' },
        { value: 'Data', label: 'Dati' },
        { value: 'Analytics', label: 'Analytics' },
        { value: 'Diagnostics', label: 'Diagnostica' },
        { value: 'Governance', label: 'Governance' },
        { value: 'Trace', label: 'Trace' },
    ],
};
export const FIELDS: FieldDef[] = [STATUS_FIELD, VISIBILITY_FIELD, CATALOG_FIELD, TYPE_FIELD];

/** Valori ammessi da AdminController (ValidAudiences). */
export const AUDIENCES: { value: string; label: string }[] = [
    { value: 'All', label: 'Tutti' },
    { value: 'Business', label: 'Utenti business' },
    { value: 'Consultant', label: 'Consulenti' },
    { value: 'Developer', label: 'Sviluppatori' },
    { value: 'Admin', label: 'Amministratori' },
    { value: 'Governance', label: 'Governance' },
];

export const optionLabel = (field: FieldDef, value: string) => field.options.find(o => o.value === value)?.label ?? value;

export type SegmentState = 'published' | 'draft' | 'outdated' | 'none';
export const SEGMENT_LABEL: Record<SegmentState, string> = {
    published: 'pubblicata', draft: 'in bozza', outdated: 'da aggiornare', none: 'assente',
};
export const CONTENT_SEGMENTS = [
    { kind: 'publicPage', label: 'Pagina pubblica' },
    { kind: 'card', label: 'Scheda' },
    { kind: 'guide', label: 'Guida' },
] as const;

/** Stato di un contenuto in tutte le lingue: da aggiornare > bozza > pubblicato > assente. */
export const segmentState = (tool: AdminTool, kind: string): SegmentState => {
    const slots = (tool.contentSlots ?? []).filter(s => s.kind === kind);
    if (slots.some(s => s.isOutdated)) return 'outdated';
    if (slots.some(s => s.draft)) return 'draft';
    if (slots.some(s => s.published)) return 'published';
    // Finché la guida a blocchi non genera il PDF, un PDF caricato conta come guida pubblicata.
    if (kind === 'guide' && tool.guideLanguages.length > 0) return 'published';
    return 'none';
};

export const moduleOf = (code: string) => (code.startsWith('custom-') ? 'Custom' : 'Standard');

/** Ordine del catalogo, lo stesso della griglia e del portale. */
export const byCatalogOrder = (a: AdminTool, b: AdminTool) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);

/** Icona del tool come nel portale: SVG del pacchetto, poi immagine caricata in admin. */
export const toolIconSrc = (tool: Pick<AdminTool, 'code' | 'imageRoles'>, version = 0): string | null =>
    TOOL_BRAND_ASSETS[tool.code]?.icon
    ?? (tool.imageRoles?.includes('tab') ? `${apiService.toolImageUrl(tool.code, 'tab')}${version ? `?v=${version}` : ''}` : null);

export const hasBrandIcon = (code: string) => Boolean(TOOL_BRAND_ASSETS[code]?.icon);

const initials = (name: string) =>
    name.split(/\s+/).filter(w => /[A-Za-z0-9]/.test(w[0] ?? '')).slice(0, 2).map(w => w[0]).join('').toUpperCase();

/** Icona del tool, o le iniziali colorate quando non ne ha una. */
export const ToolIcon: React.FC<{ tool: Pick<AdminTool, 'id' | 'code' | 'name' | 'imageRoles'>; size?: number; version?: number }> = ({ tool, size = 36, version }) => {
    const src = toolIconSrc(tool, version);
    const style = { width: size, height: size, flexBasis: size, fontSize: Math.round(size * 0.35) };
    return src
        ? <img className="cat-tool__icon" src={src} alt="" style={style} />
        : <span className={`cat-tool__icon cat-tool__icon--${tool.id % 6}`} style={style} aria-hidden="true">{initials(tool.name)}</span>;
};
